import { useState } from 'react';
import { splitPrompt } from '@shared/rules';
import type { RoundRecap } from '@shared/types';
import { FilledPrompt } from '../../components/Cards';
import { Avatar, whatsappLink } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { ShareWinButton } from '../../components/ShareWinButton';
import type { RoomApi } from '../../lib/useRoom';
import { TeamStandings, championNames } from './Modes';

function recapLine(r: RoundRecap): string {
  const answers = r.winners[0]?.cards.map((c) => c.text) ?? [];
  const parts = splitPrompt(r.prompt, answers);
  const text = parts.map((p) => (p.kind === 'text' ? p.value : p.kind === 'fill' ? `*${p.value}*` : '___')).join('');
  const hasBlank = parts.some((p) => p.kind !== 'text');
  return hasBlank ? text : `${text} *${answers.join(', ')}*`;
}

export function GameOver({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const [busy, setBusy] = useState(false);
  const isHost = view.me?.id === view.hostId;
  const ranked = [...view.players].sort((a, b) => b.score - a.score);
  const champions = championNames(view);
  const podium = [ranked[1], ranked[0], ranked[2]];
  // Players behind the win: the top scorers, or everyone on the winning team.
  const winningPlayers = view.teams.length
    ? view.players.filter((p) => p.team != null && champions.includes(view.teams[p.team]?.name ?? ''))
    : view.players.filter((p) => champions.includes(p.name));
  const topScore = view.teams.length ? Math.max(0, ...view.teams.map((t) => t.score)) : Math.max(0, ...view.players.map((p) => p.score));
  // The champion's latest winning card makes the share image.
  const best = view.recap.find((r) => r.winners.some((w) => winningPlayers.some((p) => p.name === w.playerName))) ?? view.recap[0];
  const share = `Tonight's CompleteMasti highlights 😂\n\n${view.recap
    .slice(0, 5)
    .map((r) => `• ${recapLine(r)}`)
    .join('\n')}\n\nPlay with us: ${window.location.origin}`;

  return (
    <>
      <main className="game-main" style={{ maxWidth: 760 }}>
        <section className="center">
          <h1 style={{ marginBottom: 4 }}>{champions.length ? `${champions.join(' & ')} ${champions.length > 1 || view.teams.length ? 'win' : 'wins'}!` : 'Game over'}</h1>
          <p className="muted">Complete masti, as promised.</p>
          <TeamStandings view={view} />
          <div className="podium">
            {podium.map((p, i) =>
              p ? (
                <div key={p.id} className={`step ${['second', 'first', 'third'][i]}`}>
                  <Avatar player={p} size="lg" />
                  <span>{p.name}</span>
                  <div className="block">{p.score}</div>
                </div>
              ) : (
                <div key={i} className="step" />
              ),
            )}
          </div>
        </section>

        {ranked.length > 3 && (
          <section className="panel">
            <ul className="player-list">
              {ranked.slice(3).map((p, i) => (
                <li key={p.id}>
                  <strong>{i + 4}</strong>
                  <Avatar player={p} />
                  <span className="name">{p.name}</span>
                  <strong>{p.score}</strong>
                </li>
              ))}
            </ul>
          </section>
        )}

        {view.recap.length > 0 && (
          <section className="stack">
            <div className="row between">
              <h2 style={{ margin: 0 }}>Best of the night</h2>
              <div className="row">
                {best && champions.length > 0 && (
                  <ShareWinButton win={{ prompt: best.prompt, answers: best.winners[0]?.cards.map((c) => c.text) ?? [], winner: champions.join(' & '), kind: 'game', score: topScore }} />
                )}
                <a className="btn btn-sm btn-outline" href={whatsappLink(share)} target="_blank" rel="noreferrer" data-cta="share_game_whatsapp">
                  Share on WhatsApp
                </a>
              </div>
            </div>
            {view.recap.map((r) => (
              <div key={r.number} className="recap-item stack" style={{ gap: 6 }}>
                <FilledPrompt prompt={r.prompt} answers={r.winners[0]?.cards.map((c) => c.text) ?? []} />
                <span className="muted small">
                  {r === view.recap[0] && view.round?.number === r.number ? 'Final round' : `Round ${r.number}`} ·{' '}
                  {r.winners.map((w) => w.playerName).join(' & ')}
                  {r.worst && ' · 🥴 Sabse Bekaar'}
                  {r.writtenBy && ` · ✍️ by ${r.writtenBy}`}
                </span>
              </div>
            ))}
          </section>
        )}
      </main>
      <div className="action-bar">
        <div className="inner">
          {isHost ? (
            <button
              className="btn btn-primary btn-lg btn-block"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const err = await room.act('game:restart', {});
                setBusy(false);
                if (err) toast(err, 'error');
              }}
            >
              Play again
            </button>
          ) : (
            <p className="status-line center grow" style={{ margin: 0 }}>
              Waiting for the host to start another game...
            </p>
          )}
        </div>
      </div>
    </>
  );
}
